'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { StoryService } from '@/contexts/stories/services/StoryService';
import { EDIT_FORBIDDEN, guardBlockVoice, guardEdit } from '@/contexts/pipeline-v2/edits/guardAction.server';
import type { Story, TextBlock } from '@/contexts/stories/types/story';

export async function addBlock(
  storyId: string,
  text: string,
  afterBlockId?: string | null,
): Promise<ActionResult<TextBlock>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const blocked = await guardEdit({ storyId, kind: 'add_block' });
    if (blocked) return { success: false, error: blocked.message, code: EDIT_FORBIDDEN };

    const block = await StoryService.addBlock({
      storyId,
      familyId,
      text,
      afterBlockId,
    });

    return { success: true, data: block as unknown as TextBlock };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('blocks', e) };
  }
}

export async function updateBlock(
  storyId: string,
  blockId: string,
  text: string,
): Promise<ActionResult<Story>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const blocked = await guardEdit({ storyId, kind: 'block_text' });
    if (blocked) return { success: false, error: blocked.message, code: EDIT_FORBIDDEN };

    const story = await StoryService.updateBlock({
      storyId,
      familyId,
      blockId,
      text,
    });

    return { success: true, data: story as unknown as Story };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('blocks', e) };
  }
}

export async function deleteBlock(
  storyId: string,
  blockId: string,
): Promise<ActionResult<Story>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const blocked = await guardEdit({ storyId, kind: 'delete_block' });
    if (blocked) return { success: false, error: blocked.message, code: EDIT_FORBIDDEN };

    const story = await StoryService.deleteBlock({
      storyId,
      familyId,
      blockId,
    });

    return { success: true, data: story as unknown as Story };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('blocks', e) };
  }
}

export async function reorderBlocks(
  storyId: string,
  blockIds: string[],
): Promise<ActionResult<Story>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const blocked = await guardEdit({ storyId, kind: 'reorder' });
    if (blocked) return { success: false, error: blocked.message, code: EDIT_FORBIDDEN };

    const story = await StoryService.reorderBlocks({
      storyId,
      familyId,
      blockIds,
    });

    return { success: true, data: story as unknown as Story };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('blocks', e) };
  }
}

export async function setBlockVoice(
  storyId: string,
  blockId: string,
  voiceId: string,
  voiceName: string,
): Promise<ActionResult<{ blockId: string }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    // one model per audio: a block voice must read with the narration's model
    const blocked = await guardBlockVoice({
      familyId, storyId, voiceId,
      narrationVoiceId: await narrationVoiceOfStory(storyId, familyId),
      language: await storyLanguage(storyId),
    });
    if (blocked) return { success: false, error: blocked.message, code: EDIT_FORBIDDEN };

    const result = await StoryService.setBlockVoice({
      storyId,
      familyId,
      blockId,
      voiceId,
      voiceName,
    });

    return { success: true, data: result };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('blocks', e) };
  }
}

/** The voice the story's newest narration reads with (null: no narration yet). */
async function narrationVoiceOfStory(storyId: string, familyId: string): Promise<string | null> {
  const { getAudioStoryRepository } = await import('@/contexts/audio-stories/repositories');
  const list = await getAudioStoryRepository().getByStoryId(storyId, familyId).catch(() => []);
  const newest = [...list].sort((a, b) => String(b.updatedAt ?? '').localeCompare(String(a.updatedAt ?? '')))[0];
  return newest?.defaultVoiceId ?? null;
}

async function storyLanguage(storyId: string): Promise<string> {
  const { getStoryRepository } = await import('@/contexts/stories/repositories');
  return (await getStoryRepository().getById(storyId).catch(() => null))?.language ?? 'ru';
}
